// src/components/SimpleFormComponents/SimpleAddressForm.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import React, { useEffect, useState } from 'react';
import { Col, Row, Form } from 'antd';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import { useSimpleFormContext } from '../SimpleFormProvider';
import SimpleCountry from 'components/SimpleComponents/SimpleCountry';
import SimpleAddressFormBR from 'components/SimpleComponents/SimpleEntityForm/br/SimpleAddressFormBR';
import SimpleAddressFormPT from 'components/SimpleComponents/SimpleEntityForm/pt/SimpleAddressFormPT';
import SimpleAddressFormUS from 'components/SimpleComponents/SimpleEntityForm/us/SimpleAddressFormUS';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const FIELD_SPAN = { xs: 24, sm: 12, lg: 6 }; // keeps equal width across breakpoints

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleAddressForm = ({ parentCountryCode, hideAddressCountrySelector, ...props }) => {
  // ---------------------------------------------------------------------------
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------
  const { form } = useSimpleFormContext?.() || {};
  const { subdocumentField } = props;

  // ---------------------------------------------------------------------------
  // 2. LOCAL STATE
  // ---------------------------------------------------------------------------
  const countryFieldPath = subdocumentField ? [subdocumentField, 'countryCode'] : ['countryCode'];
  // Watch the address countryCode from form to determine which form to render
  const addressCountryCode = Form.useWatch(countryFieldPath, form);
  // User's explicit address-country override (only set via the visible selector)
  const [userPickedCountry, setUserPickedCountry] = useState(null);

  const normalizedParent = parentCountryCode ? String(parentCountryCode).toUpperCase() : null;

  // ---------------------------------------------------------------------------
  // 3. HANDLERS
  // ---------------------------------------------------------------------------
  const handleOnCountryChange = value => {
    setUserPickedCountry(value?.toUpperCase() || null);
  };

  // Precedence:
  // - Selector HIDDEN: address always follows the entity/form country (parent).
  //   A stale persisted address.countryCode must NOT override it.
  // - Selector VISIBLE: an explicit user pick wins; else the saved address
  //   country; else the entity country.
  const effectiveCountryCode = (
    userPickedCountry ||
    (hideAddressCountrySelector ? normalizedParent : addressCountryCode || normalizedParent) ||
    'BR'
  ).toUpperCase();

  // ---------------------------------------------------------------------------
  // 4. EFFECTS
  // ---------------------------------------------------------------------------
  // When the selector is hidden, keep the persisted address.countryCode in sync
  // with the entity country so save AND the postal-code lookup (handleAddressZipBlur
  // reads address.countryCode -> BR=CEP / PT=GeoAPI.pt) use the right country.
  useEffect(() => {
    if (!form || !hideAddressCountrySelector || !normalizedParent) return;
    const current = form.getFieldValue(countryFieldPath);
    if ((current ? String(current).toUpperCase() : null) !== normalizedParent) {
      form.setFieldValue(countryFieldPath, normalizedParent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, hideAddressCountrySelector, normalizedParent, addressCountryCode]);

  // ---------------------------------------------------------------------------
  // 5. UI
  // ---------------------------------------------------------------------------
  return (
    <>
      {/* COUNTRY */}
      <Row gutter={ROW_GUTTER} style={{ display: hideAddressCountrySelector ? 'none' : 'block' }}>
        <Col {...FIELD_SPAN}>
          <SimpleCountry
            name="countryCode"
            subdocumentField={subdocumentField}
            label="country"
            countryCode={parentCountryCode}
            associatedValueField="address1"
            onCountryChange={handleOnCountryChange}
          />
        </Col>
      </Row>
      {/* COUNTRY */}

      {/* ADDRESS FORM */}
      {effectiveCountryCode === 'BR' ? (
        <SimpleAddressFormBR {...props} />
      ) : effectiveCountryCode === 'PT' ? (
        <SimpleAddressFormPT {...props} />
      ) : (
        <SimpleAddressFormUS {...props} />
      )}
      {/* ADDRESS FORM */}
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleAddressForm;

// *******************************************************************************
// HELPERS
// *******************************************************************************
